(function () {
  'use strict';

  const header = document.querySelector('[data-header]');
  if (!header) return;

  const drawer = header.querySelector('[data-menu-drawer]');
  if (!drawer) return;

  const overlay = header.querySelector('[data-menu-overlay]');
  const menuHome = drawer.querySelector('[data-menu-home]');
  const utilities = drawer.querySelector('[data-menu-utilities]');

  const EASE = 'var(--ease-both)';
  const FADE_MS = 300;
  const BG_OPEN = 'bg-surface-secondary';
  const BG_SUB = 'bg-surface-primary';

  /* global createFocusTrap */
  const trap = typeof createFocusTrap === 'function' ? createFocusTrap(drawer) : null;

  // Set true for the instant of a coordinator cross-fade switch so the observer
  // callback runs only the light aria/inert + open-flag bookkeeping and skips the
  // heavy lifecycle (scroll-lock, focus-trap, fadeIn, resetToHome) — L-XF-9.
  let tabSwitching = false;

  // Timer that restores the sticky group's resting transform after a close (see close branch).
  const group = document.querySelector('[data-header-group]');
  let groupResetTimer = 0;

  // --- Helpers ---------------------------------------------------------------

  function handleize(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  function fadeOut(el, cb) {
    el.style.transition = 'opacity ' + FADE_MS + 'ms ' + EASE;
    el.style.opacity = '0';
    setTimeout(cb, FADE_MS);
  }

  function fadeIn(el) {
    el.classList.remove('hidden');
    el.style.opacity = '0';
    el.style.transition = 'opacity ' + FADE_MS + 'ms ' + EASE;
    requestAnimationFrame(function () { el.style.opacity = '1'; });
  }

  function hideEl(el) {
    el.classList.add('hidden');
    el.style.opacity = '';
    el.style.transition = '';
  }

  function showEl(el) {
    el.classList.remove('hidden');
    el.style.opacity = '';
    el.style.transition = '';
  }

  function closeDrawer() { header.dataset.menuOpen = 'false'; }

  // Apply the open-state flip without animating the panel's translate transition
  // (coordinator tab-switch path): the panel jumps on/off-screen instantly while
  // only the CONTENT cross-fades via the host CSS. Mirrors mini-bag/wishlist. JS-only.
  function withNoSlide(fn) {
    var prev = drawer.style.transition;
    drawer.style.transition = 'none';
    fn();
    void drawer.offsetWidth;                // force reflow so the no-transition write commits
    requestAnimationFrame(function () { drawer.style.transition = prev; });
  }

  // Swap only the background between home/submenu — the top padding is now a single
  // constant on the drawer (pt-(--menu-drawer-top-offset)), never swapped per state.
  function swapBg(from, to) {
    drawer.classList.remove(from);
    drawer.classList.add(to);
  }

  // --- Open / close (MutationObserver on [data-header]) ----------------------

  // Heavy open lifecycle. aria-hidden/inert are handled by the observer callback
  // (always, outside the tabSwitch guard) — never written here.
  function onOpen() {
    document.documentElement.setAttribute('data-scroll-lock', '');
    drawer.querySelectorAll('[data-drawer-scroll]').forEach(function (s) { s.scrollTop = 0; }); // open from the top
    // Focus the dialog panel itself (tabindex="-1"), not the first link — focusing the
    // first item paints iOS Safari's blue focus box around it (the highlighted "New In").
    if (trap) { trap.activate(); drawer.focus(); }
    if (menuHome) fadeIn(menuHome);
    if (utilities) fadeIn(utilities);
  }

  function resetToHome() {
    const panels = drawer.querySelectorAll('[data-submenu-panel]');
    for (let i = 0; i < panels.length; i++) hideEl(panels[i]);
    swapBg(BG_SUB, BG_OPEN);
    if (menuHome) showEl(menuHome);
    if (utilities) showEl(utilities);
  }

  // Heavy close lifecycle. aria-hidden/inert are handled by the observer callback
  // (always, outside the tabSwitch guard) — never written here.
  function onClose() {
    document.documentElement.removeAttribute('data-scroll-lock');
    if (trap) trap.deactivate();
    const menuToggle = header.querySelector('[data-menu-toggle]');
    if (menuToggle) menuToggle.focus();
    resetToHome();
  }

  const observer = new MutationObserver(function (mutations) {
    for (let i = 0; i < mutations.length; i++) {
      if (mutations[i].attributeName !== 'data-menu-open') continue;
      const opening = header.dataset.menuOpen === 'true';
      // Light bookkeeping — ALWAYS, even on a coordinator switch (INVARIANTS §1
      // rule 6: paired aria-hidden + inert; expose via removeAttribute, never the
      // false value). MQ-3.
      if (opening) {
        drawer.removeAttribute('aria-hidden');
        drawer.removeAttribute('inert');
        // Re-opened mid-close: cancel the pending snap-back (the :has() override takes over).
        clearTimeout(groupResetTimer);
      } else {
        drawer.setAttribute('aria-hidden', 'true');
        drawer.setAttribute('inert', '');
        // A transform on the group makes it the containing block for the fixed drawer +
        // overlay. The :has() open override keeps it transform-neutral but drops when
        // data-menu-open flips false — while they still animate out. Hold it inline through
        // the close, then snap back with no transition. Mirrors menu-mega.js.
        if (group && group.hasAttribute('data-header-sticky')) {
          group.style.transition = 'none';
          group.style.translate = 'none';
          group.style.top = 'calc(-1 * var(--announcement-bar-height))';
          clearTimeout(groupResetTimer);
          groupResetTimer = setTimeout(function () {
            group.style.translate = '';
            group.style.top = '';
            void group.offsetWidth;               // commit the resting transform instantly
            requestAnimationFrame(function () { group.style.transition = ''; });
          }, FADE_MS);
        }
      }
      // On a coordinator cross-fade, skip the heavy lifecycle (L-XF-9).
      if (tabSwitching) continue;
      if (opening) onOpen(); else onClose();
    }
  });
  observer.observe(header, { attributes: true, attributeFilter: ['data-menu-open'] });

  // --- Public API — the drawer coordinator drives the mobile cross-fade -----
  // openMenu/closeMenu write data-menu-open on [data-header] (menu's open-state
  // lives there, not on the drawer root). On the tabSwitch path the flip is
  // slide-suppressed and the observer skips its heavy branch (tabSwitching flag,
  // cleared on the next frame — after the observer microtask). MQ-2: a tabSwitch
  // close does NOT resetToHome, so submenu state survives a switch-back.
  function openMenu(opts) {
    if (header.dataset.menuOpen === 'true') return;
    if (opts && opts.tabSwitch) {
      tabSwitching = true;
      withNoSlide(function () { header.dataset.menuOpen = 'true'; });
      requestAnimationFrame(function () { tabSwitching = false; });
    } else {
      header.dataset.menuOpen = 'true';
    }
  }
  function closeMenu(opts) {
    if (header.dataset.menuOpen !== 'true') return;
    // Reset the hamburger↔X morph + ARIA here on the programmatic close path
    // (coordinator cross-fade). header.js only resets these on a DIRECT toggle
    // click; a switch to another drawer closes the menu through this API instead,
    // so without this reset aria-expanded stays "true" and the toggle keeps
    // showing an X after that other drawer is later closed.
    const menuToggle = header.querySelector('[data-menu-toggle]');
    if (menuToggle) {
      menuToggle.setAttribute('aria-expanded', 'false');
      menuToggle.setAttribute('aria-label', 'Open menu');
    }
    if (opts && opts.tabSwitch) {
      tabSwitching = true;
      withNoSlide(function () { header.dataset.menuOpen = 'false'; });
      requestAnimationFrame(function () { tabSwitching = false; });
    } else {
      header.dataset.menuOpen = 'false';
    }
  }
  function isMenuOpen() { return header.dataset.menuOpen === 'true'; }
  window.broraMenuDrawer = { open: openMenu, close: closeMenu, isOpen: isMenuOpen };

  // --- Event delegation on drawer --------------------------------------------

  drawer.addEventListener('click', function (e) {
    // Drill-down: nav link with children
    const link = e.target.closest('[data-menu-link][data-has-children="true"]');
    if (link) {
      e.preventDefault();
      const handle = handleize(link.textContent.trim());
      const panel = drawer.querySelector('[data-submenu-panel="' + handle + '"]');
      if (!panel) return;
      if (utilities) {
        utilities.style.transition = 'opacity ' + FADE_MS + 'ms ' + EASE;
        utilities.style.opacity = '0';
      }
      fadeOut(menuHome, function () {
        hideEl(menuHome);
        if (utilities) hideEl(utilities);
        swapBg(BG_OPEN, BG_SUB);
        fadeIn(panel);
      });
      return;
    }

    // Back button
    const back = e.target.closest('[data-menu-back]');
    if (back) {
      const activePanel = back.closest('[data-submenu-panel]');
      if (!activePanel) return;
      fadeOut(activePanel, function () {
        hideEl(activePanel);
        swapBg(BG_SUB, BG_OPEN);
        fadeIn(menuHome);
        if (utilities) fadeIn(utilities);
      });
    }
  });

  // --- Overlay click ---------------------------------------------------------

  if (overlay) overlay.addEventListener('click', closeDrawer);

  // --- Escape key ------------------------------------------------------------

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && header.dataset.menuOpen === 'true') closeDrawer();
  });
})();
